import { useCallback, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import CustomModal from "../../../components/CustomComponents/modal/customModal";
import showErrorToast from "../../../components/CustomComponents/toast/toast";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import useDarkMode from "../../../hooks/useDarkMode";
import useThemeColor from "../../../hooks/useThemeColor";
import useDebouncedEffect from "../../../hooks/useDebouncedEffect";

const CHANNELS = [
  {
    id: "Email",
    label: "Email",
    iconClass: "fa-solid fa-envelope",
    hint: "Send branded HTML emails to your audience.",
  },
  {
    id: "Sms",
    label: "SMS",
    iconClass: "fa-solid fa-comment-sms",
    hint: "Send text messages to customers with a mobile number.",
  },
  {
    id: "WhatsApp",
    label: "WhatsApp",
    iconClass: "fa-brands fa-whatsapp",
    hint: "Reach customers on WhatsApp using your SMS preset content.",
  },
];

const truncateText = (text, max = 72) => {
  if (!text) return "";
  const trimmed = String(text).trim();
  return trimmed.length <= max ? trimmed : `${trimmed.slice(0, max)}…`;
};

const GroupOptionLabel = ({ option, context, mutedText, themeColor }) => {
  const isMenu = context === "menu";
  const count = option.customerCount ?? 0;

  if (!isMenu) {
    return (
      <span className="d-flex align-items-center gap-2">
        <i className="fa-solid fa-users" style={{ color: themeColor, fontSize: "0.85rem" }} />
        <span className="text-truncate">{option.label}</span>
        <span
          className="badge rounded-pill ms-auto"
          style={{
            backgroundColor: `${themeColor}22`,
            color: themeColor,
            fontSize: "0.7rem",
          }}
        >
          {count}
        </span>
      </span>
    );
  }

  return (
    <div className="py-1">
      <div className="d-flex align-items-start gap-2">
        <span
          className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
          style={{
            width: 32,
            height: 32,
            backgroundColor: `${themeColor}18`,
            color: themeColor,
          }}
        >
          <i className="fa-solid fa-users" style={{ fontSize: "0.85rem" }} />
        </span>
        <div className="flex-grow-1 min-w-0">
          <div className="d-flex align-items-center gap-2">
            <span className="fw-semibold text-truncate">{option.label}</span>
            <span
              className="badge rounded-pill flex-shrink-0"
              style={{
                backgroundColor: `${themeColor}22`,
                color: themeColor,
                fontSize: "0.68rem",
              }}
            >
              {count} {count === 1 ? "customer" : "customers"}
            </span>
          </div>
          {option.description ? (
            <div className="small text-truncate mt-1" style={{ color: mutedText }}>
              {truncateText(option.description, 90)}
            </div>
          ) : (
            <div className="small mt-1" style={{ color: mutedText }}>
              Marketing audience group
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const PresetOptionLabel = ({ option, context, mutedText, themeColor, isEmail }) => {
  const isMenu = context === "menu";
  const iconClass = isEmail ? "fa-solid fa-envelope" : "fa-solid fa-comment-dots";
  const subtitle = isEmail ? option.subject : option.message;

  if (!isMenu) {
    return (
      <span className="d-flex align-items-center gap-2 min-w-0">
        <i className={iconClass} style={{ color: themeColor, fontSize: "0.85rem" }} />
        <span className="text-truncate">{option.label}</span>
      </span>
    );
  }

  return (
    <div className="py-1">
      <div className="d-flex align-items-start gap-2">
        <span
          className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
          style={{
            width: 32,
            height: 32,
            backgroundColor: `${themeColor}18`,
            color: themeColor,
          }}
        >
          <i className={iconClass} style={{ fontSize: "0.85rem" }} />
        </span>
        <div className="flex-grow-1 min-w-0">
          <div className="fw-semibold text-truncate">{option.label}</div>
          {subtitle ? (
            <div
              className="small mt-1"
              style={{
                color: mutedText,
                lineHeight: 1.35,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {isEmail ? `Subject: ${truncateText(subtitle, 100)}` : truncateText(subtitle, 100)}
            </div>
          ) : (
            <div className="small mt-1" style={{ color: mutedText }}>
              {isEmail ? "Email template" : "SMS / WhatsApp template"}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function SetupInfoItem({ iconClass, label, value, meta, themeColor, isDarkMode, panelBorder }) {
  const mutedText = isDarkMode ? "#adb5bd" : "#6c757d";
  const fieldBg = isDarkMode ? "#2d3436" : "#ffffff";

  return (
    <div
      className="d-flex align-items-center gap-2 rounded-2 px-2 py-2 flex-fill"
      style={{
        backgroundColor: fieldBg,
        border: `1px solid ${panelBorder}`,
        minWidth: "140px",
      }}
    >
      <span
        className="d-flex align-items-center justify-content-center rounded-2 flex-shrink-0"
        style={{
          width: 28,
          height: 28,
          backgroundColor: `${themeColor}18`,
          color: themeColor,
        }}
      >
        <i className={iconClass} style={{ fontSize: "0.75rem" }} />
      </span>
      <div className="flex-grow-1 min-w-0">
        <div
          className="text-uppercase fw-semibold"
          style={{ fontSize: "0.6rem", letterSpacing: "0.04em", color: mutedText, lineHeight: 1.2 }}
        >
          {label}
        </div>
        <div className="fw-semibold text-truncate" style={{ fontSize: "0.82rem", lineHeight: 1.25 }}>
          {value || "—"}
        </div>
        {meta && (
          <div
            className="text-truncate"
            style={{ color: mutedText, fontSize: "0.72rem", lineHeight: 1.2 }}
            title={meta}
          >
            {meta}
          </div>
        )}
      </div>
    </div>
  );
}

function CampaignSetupReadOnly({
  channel,
  groupTitle,
  groupCount,
  presetTitle,
  presetSubtext,
  themeColor,
  isDarkMode,
  panelBg,
  panelBorder,
}) {
  const activeChannel = CHANNELS.find((ch) => ch.id === channel);
  const audienceMeta =
    groupCount != null
      ? `${groupCount} ${groupCount === 1 ? "customer" : "customers"}`
      : null;

  return (
    <div
      className="rounded-3 px-3 py-2"
      style={{
        backgroundColor: panelBg,
        border: `1px solid ${panelBorder}`,
      }}
    >
      <div className="fw-semibold small mb-2" style={{ fontSize: "0.78rem" }}>
        Campaign setup
      </div>

      <div className="d-flex flex-wrap gap-2">
        <SetupInfoItem
          iconClass={activeChannel?.iconClass ?? "fa-solid fa-bullhorn"}
          label="Channel"
          value={activeChannel?.label ?? channel}
          themeColor={themeColor}
          isDarkMode={isDarkMode}
          panelBorder={panelBorder}
        />
        <SetupInfoItem
          iconClass="fa-solid fa-users"
          label="Audience"
          value={groupTitle || "Marketing group"}
          meta={audienceMeta}
          themeColor={themeColor}
          isDarkMode={isDarkMode}
          panelBorder={panelBorder}
        />
        <SetupInfoItem
          iconClass={
            channel === "Email" ? "fa-solid fa-envelope" : "fa-solid fa-comment-dots"
          }
          label="Template"
          value={presetTitle || "—"}
          meta={presetSubtext ? truncateText(presetSubtext, 48) : null}
          themeColor={themeColor}
          isDarkMode={isDarkMode}
          panelBorder={panelBorder}
        />
      </div>
    </div>
  );
}

const emptyForm = {
  channel: "Email",
  marketingGroup_Guid: null,
  smsPreset_Guid: null,
  emailPreset_Guid: null,
  name: "",
  description: "",
};

const normalizeGroupOption = (row) => {
  const label = row.groupTitle ?? row.GroupTitle ?? "Group";
  const description = row.description ?? row.Description ?? "";
  return {
    value: row.marketingGroup_Guid ?? row.MarketingGroup_Guid,
    label,
    description,
    customerCount: row.customerCount ?? row.CustomerCount ?? 0,
    searchText: `${label} ${description}`.toLowerCase(),
  };
};

const normalizeSmsPresetOption = (row) => {
  const label = row.presetTitle ?? row.PresetTitle ?? "Preset";
  const message = row.message ?? row.Message ?? "";
  return {
    value: row.smsPreset_Guid ?? row.SmsPreset_Guid,
    label,
    message,
    searchText: `${label} ${message}`.toLowerCase(),
  };
};

const normalizeEmailPresetOption = (row) => {
  const label = row.presetTitle ?? row.PresetTitle ?? "Preset";
  const subject = row.subject ?? row.Subject ?? "";
  return {
    value: row.emailPreset_Guid ?? row.EmailPreset_Guid,
    label,
    subject,
    searchText: `${label} ${subject}`.toLowerCase(),
  };
};

function CampaignModel({
  isModalOpen,
  setIsModalOpen,
  onCampaignSaved,
  compID,
  campaignToEdit = null,
}) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [groupOptions, setGroupOptions] = useState([]);
  const [smsPresetOptions, setSmsPresetOptions] = useState([]);
  const [emailPresetOptions, setEmailPresetOptions] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const isEditing = !!campaignToEdit;
  const isEmailChannel = form.channel === "Email";
  const hasChannel = !!form.channel;
  const activeChannel = CHANNELS.find((ch) => ch.id === form.channel);

  const presetGuid = isEmailChannel ? form.emailPreset_Guid : form.smsPreset_Guid;
  const canPreview = hasChannel && form.marketingGroup_Guid && presetGuid;

  const selectStyles = useMemo(
    () => ({
      control: (base, state) => ({
        ...base,
        minHeight: "42px",
        backgroundColor: isDarkMode ? "#2d3436" : "#ffffff",
        borderRadius: "10px",
        borderColor: state.isFocused
          ? themeColor
          : isDarkMode
            ? "#495057"
            : "#dee2e6",
        boxShadow: state.isFocused ? `0 0 0 1px ${themeColor}` : "none",
        color: isDarkMode ? "#fff" : base.color,
        opacity: hasChannel ? 1 : 0.65,
      }),
      menu: (base) => ({
        ...base,
        backgroundColor: isDarkMode ? "#2d3436" : base.backgroundColor,
        zIndex: 10050,
        borderRadius: "12px",
        overflow: "hidden",
        boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
      }),
      menuPortal: (base) => ({
        ...base,
        zIndex: 10060,
      }),
      option: (base, state) => ({
        ...base,
        padding: "8px 12px",
        cursor: "pointer",
        backgroundColor: state.isSelected
          ? isDarkMode
            ? `${themeColor}33`
            : `${themeColor}14`
          : state.isFocused
            ? isDarkMode
              ? "#495057"
              : "#f8f9fa"
            : isDarkMode
              ? "#2d3436"
              : base.backgroundColor,
        color: isDarkMode ? "#fff" : base.color,
      }),
      menuList: (base) => ({
        ...base,
        padding: "6px",
        maxHeight: "240px",
      }),
      singleValue: (base) => ({
        ...base,
        color: isDarkMode ? "#fff" : base.color,
      }),
      input: (base) => ({
        ...base,
        color: isDarkMode ? "#fff" : base.color,
      }),
      placeholder: (base) => ({
        ...base,
        color: isDarkMode ? "#adb5bd" : "#6c757d",
      }),
    }),
    [hasChannel, isDarkMode, themeColor]
  );

  const loadOptions = useCallback(async () => {
    if (!compID) return;
    try {
      setLoadingOptions(true);
      const [groupsRes, smsRes, emailRes] = await Promise.all([
        axiosInstance.get(APIs.GET_MARKETING_GROUPS, {
          params: { CompID: compID, PageSize: 500, PageNumber: 1 },
        }),
        axiosInstance.get(APIs.GET_SMS_PRESETS, {
          params: { CompID: compID, PageSize: 500, PageNumber: 1 },
        }),
        axiosInstance.get(APIs.GET_EMAIL_PRESETS, {
          params: { CompID: compID, PageSize: 500, PageNumber: 1 },
        }),
      ]);

      const groups = groupsRes.data?.data ?? groupsRes.data?.Data ?? [];
      const smsPresets = smsRes.data?.data ?? smsRes.data?.Data ?? [];
      const emailPresets = emailRes.data?.data ?? emailRes.data?.Data ?? [];

      setGroupOptions(Array.isArray(groups) ? groups.map(normalizeGroupOption) : []);
      setSmsPresetOptions(Array.isArray(smsPresets) ? smsPresets.map(normalizeSmsPresetOption) : []);
      setEmailPresetOptions(Array.isArray(emailPresets) ? emailPresets.map(normalizeEmailPresetOption) : []);
    } catch {
      showErrorToast("Failed to load campaign options.");
    } finally {
      setLoadingOptions(false);
    }
  }, [compID]);

  const loadPreview = useCallback(async () => {
    if (!compID || !canPreview) {
      setPreview(null);
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await axiosInstance.post(APIs.POST_PREVIEW_CAMPAIGN, {
        compID: Number(compID),
        channel: form.channel,
        marketingGroup_Guid: form.marketingGroup_Guid,
        smsPreset_Guid: isEmailChannel ? null : form.smsPreset_Guid,
        emailPreset_Guid: isEmailChannel ? form.emailPreset_Guid : null,
      });
      const data = response.data?.data ?? response.data?.Data ?? null;
      setPreview(data);
    } catch {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [compID, canPreview, form.channel, form.marketingGroup_Guid, form.smsPreset_Guid, form.emailPreset_Guid, isEmailChannel]);

  useEffect(() => {
    if (!isModalOpen || isEditing) return;
    loadOptions();
  }, [isModalOpen, isEditing, loadOptions]);

  useEffect(() => {
    if (!isModalOpen) return;

    if (campaignToEdit) {
      setForm({
        channel: campaignToEdit.channel ?? campaignToEdit.Channel ?? "",
        marketingGroup_Guid:
          campaignToEdit.marketingGroup_Guid ?? campaignToEdit.MarketingGroup_Guid ?? null,
        smsPreset_Guid: campaignToEdit.smsPreset_Guid ?? campaignToEdit.SmsPreset_Guid ?? null,
        emailPreset_Guid: campaignToEdit.emailPreset_Guid ?? campaignToEdit.EmailPreset_Guid ?? null,
        name: campaignToEdit.name ?? campaignToEdit.Name ?? "",
        description: campaignToEdit.description ?? campaignToEdit.Description ?? "",
      });
    } else {
      setForm(emptyForm);
    }
    setPreview(null);
  }, [campaignToEdit, isModalOpen]);

  useDebouncedEffect(() => {
    if (isModalOpen && !isEditing) loadPreview();
  }, [isModalOpen, isEditing, form.channel, form.marketingGroup_Guid, form.smsPreset_Guid, form.emailPreset_Guid], 400);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setForm(emptyForm);
    setPreview(null);
  };

  const handleChannelSelect = (channel) => {
    setForm((prev) => ({
      ...prev,
      channel,
      smsPreset_Guid: channel === "Email" ? null : prev.smsPreset_Guid,
      emailPreset_Guid: channel === "Email" ? prev.emailPreset_Guid : null,
    }));
    setPreview(null);
  };

  const validateForm = () => {
    if (!isEditing) {
      if (!form.channel) {
        showErrorToast("Please select a campaign channel.");
        return false;
      }
      if (!form.marketingGroup_Guid) {
        showErrorToast("Please select a marketing group.");
        return false;
      }
      if (isEmailChannel && !form.emailPreset_Guid) {
        showErrorToast("Please select an email preset.");
        return false;
      }
      if (!isEmailChannel && !form.smsPreset_Guid) {
        showErrorToast("Please select an SMS preset.");
        return false;
      }
    }
    if (!form.name?.trim()) {
      showErrorToast("Please provide a campaign name.");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      const payload = {
        campaign_Guid: campaignToEdit?.campaign_Guid ?? campaignToEdit?.Campaign_Guid ?? null,
        compID: Number(compID),
        name: form.name.trim(),
        description: form.description?.trim() || null,
        channel: form.channel,
        marketingGroup_Guid: form.marketingGroup_Guid,
        smsPreset_Guid: isEmailChannel ? null : form.smsPreset_Guid,
        emailPreset_Guid: isEmailChannel ? form.emailPreset_Guid : null,
      };

      const response = await axiosInstance.post(APIs.POST_ADD_UPDATE_CAMPAIGN, payload);
      if (!response.data?.status) {
        showErrorToast(response.data?.message || "Failed to save campaign.");
        return;
      }

      const saved = response.data?.data ?? response.data?.Data;
      onCampaignSaved?.(saved, isEditing);
      handleCloseModal();
    } catch (err) {
      showErrorToast(err.response?.data?.message || "Failed to save campaign.");
    } finally {
      setSaving(false);
    }
  };

  const selectedGroup = groupOptions.find((g) => g.value === form.marketingGroup_Guid);
  const presetOptions = isEmailChannel ? emailPresetOptions : smsPresetOptions;
  const selectedPreset = presetOptions.find(
    (p) => p.value === (isEmailChannel ? form.emailPreset_Guid : form.smsPreset_Guid)
  );

  const mutedText = isDarkMode ? "#adb5bd" : "#6c757d";
  const eligibleCount = preview?.eligibleRecipients ?? preview?.EligibleRecipients;
  const totalInGroup = preview?.totalGroupCustomers ?? preview?.TotalGroupCustomers;
  const showEligibleBadge = canPreview && (previewLoading || preview != null);

  const panelBg = isDarkMode ? "#1e2124" : "#eef1f4";
  const panelBorder = isDarkMode ? "#495057" : "#dee2e6";
  const fieldBg = isDarkMode ? "#2d3436" : "#ffffff";

  const fieldPanelStyle = {
    backgroundColor: panelBg,
    border: `1px solid ${panelBorder}`,
    borderRadius: "12px",
    padding: "16px",
  };

  const fieldInputStyle = {
    backgroundColor: fieldBg,
    borderColor: panelBorder,
    color: isDarkMode ? "#fff" : "#212529",
    borderRadius: "10px",
  };

  const editGroupTitle =
    campaignToEdit?.marketingGroupTitle ??
    campaignToEdit?.MarketingGroupTitle ??
    selectedGroup?.label ??
    "";
  const editPresetTitle =
    campaignToEdit?.presetTitle ?? campaignToEdit?.PresetTitle ?? selectedPreset?.label ?? "";
  const editPresetSubtext = isEmailChannel
    ? selectedPreset?.subject
      ? `Subject: ${truncateText(selectedPreset.subject, 100)}`
      : null
    : selectedPreset?.message
      ? truncateText(selectedPreset.message, 100)
      : null;
  const editGroupCount =
    campaignToEdit?.marketingGroupCustomerCount ??
    campaignToEdit?.MarketingGroupCustomerCount ??
    selectedGroup?.customerCount ??
    null;

  return (
    <CustomModal
      isOpen={isModalOpen}
      onClose={handleCloseModal}
      onSubmit={handleSubmit}
      title={isEditing ? "Edit campaign" : "Create campaign"}
      subtitle={
        isEditing
          ? "Update the campaign name or description."
          : "Choose a channel, audience, and preset."
      }
      showButton={true}
      isCentered={true}
      dialogMaxWidth="620px"
      maxHeight="85vh"
      submitButtonText={saving ? "Saving..." : isEditing ? "Update" : "Create"}
      submitButtonDisabled={saving || (!isEditing && !hasChannel)}
      submitButtonStyle={{
        width: "auto",
        minWidth: "108px",
        whiteSpace: "nowrap",
        padding: "8px 20px",
      }}
    >
      <div className="d-flex flex-column gap-3">
        {isEditing ? (
          <>
            <CampaignSetupReadOnly
              channel={form.channel}
              groupTitle={editGroupTitle}
              groupCount={editGroupCount}
              presetTitle={editPresetTitle}
              presetSubtext={editPresetSubtext}
              themeColor={themeColor}
              isDarkMode={isDarkMode}
              panelBg={panelBg}
              panelBorder={panelBorder}
            />

            <div style={fieldPanelStyle}>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label fw-semibold">
                    Campaign name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g. Summer loyalty offer"
                    value={form.name}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                    style={fieldInputStyle}
                  />
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">Description</label>
                  <textarea
                    className="form-control"
                    placeholder="Optional note for your team"
                    value={form.description}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, description: e.target.value }))
                    }
                    rows="2"
                    style={{ ...fieldInputStyle, resize: "vertical", minHeight: "64px" }}
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
        {/* Channel toggle */}
        <div>
          <label className="form-label fw-semibold mb-2">Channel</label>
          <div
            className="d-flex gap-1 p-1 rounded-3"
            role="radiogroup"
            aria-label="Campaign channel"
            style={{
              backgroundColor: panelBg,
              border: `1px solid ${panelBorder}`,
            }}
          >
            {CHANNELS.map((ch) => {
              const isActive = form.channel === ch.id;
              return (
                <button
                  key={ch.id}
                  type="button"
                  role="radio"
                  aria-checked={isActive}
                  className="btn flex-fill d-flex align-items-center justify-content-center gap-2"
                  style={{
                    height: "36px",
                    minHeight: "36px",
                    padding: "0 12px",
                    borderRadius: "10px",
                    border: "none",
                    backgroundColor: isActive
                      ? isDarkMode
                        ? "#2d3436"
                        : "#fff"
                      : "transparent",
                    color: isActive ? (isDarkMode ? "#fff" : "#212529") : mutedText,
                    boxShadow: isActive ? "0 1px 4px rgba(0,0,0,0.12)" : "none",
                    transition: "all 0.2s ease",
                    fontSize: "0.875rem",
                    fontWeight: isActive ? 600 : 500,
                  }}
                  onClick={() => handleChannelSelect(ch.id)}
                >
                  <i
                    className={ch.iconClass}
                    style={{
                      fontSize: "0.95rem",
                      color: isActive ? themeColor : mutedText,
                    }}
                  />
                  <span>{ch.label}</span>
                </button>
              );
            })}
          </div>
          <p className="mb-0 mt-2 small" style={{ color: mutedText }}>
            {activeChannel
              ? activeChannel.hint
              : "Select Email, SMS, or WhatsApp to configure your campaign."}
          </p>
        </div>

        {/* Campaign details */}
        <div
          style={{
            ...fieldPanelStyle,
            opacity: hasChannel ? 1 : 0.5,
            pointerEvents: hasChannel ? "auto" : "none",
            transition: "opacity 0.2s ease",
          }}
        >
          <div className="row g-3">
            <div className="col-12">
              <label className="form-label fw-semibold">
                Campaign name <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className="form-control"
                placeholder="e.g. Summer loyalty offer"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                disabled={!hasChannel}
                style={fieldInputStyle}
              />
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold">Description</label>
              <textarea
                className="form-control"
                placeholder="Optional note for your team"
                value={form.description}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, description: e.target.value }))
                }
                rows="2"
                disabled={!hasChannel}
                style={{ ...fieldInputStyle, resize: "vertical", minHeight: "64px" }}
              />
            </div>

            <div className="col-12">
              <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                <label className="form-label fw-semibold mb-0">
                  Audience <span className="text-danger">*</span>
                </label>
                {showEligibleBadge && (
                  <span className="small flex-shrink-0" style={{ color: mutedText }}>
                    {previewLoading ? (
                      <>
                        <i className="fa fa-spinner fa-spin me-1" />
                        Checking…
                      </>
                    ) : (
                      <>
                        Eligible:{" "}
                        <strong style={{ color: themeColor }}>{eligibleCount ?? 0}</strong>
                        {" / "}
                        {totalInGroup ?? selectedGroup?.customerCount ?? 0} in group
                      </>
                    )}
                  </span>
                )}
              </div>
              <Select
                options={groupOptions}
                value={selectedGroup ?? null}
                onChange={(opt) =>
                  setForm((prev) => ({ ...prev, marketingGroup_Guid: opt?.value ?? null }))
                }
                styles={selectStyles}
                placeholder={loadingOptions ? "Loading groups..." : "Search or select audience..."}
                isDisabled={!hasChannel || loadingOptions}
                isClearable
                isSearchable
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPlacement="auto"
                filterOption={(option, input) =>
                  !input?.trim() ||
                  (option.data.searchText || option.label.toLowerCase()).includes(
                    input.trim().toLowerCase()
                  )
                }
                noOptionsMessage={() => "No marketing groups found"}
                formatOptionLabel={(opt, meta) => (
                  <GroupOptionLabel
                    option={opt}
                    context={meta.context}
                    mutedText={mutedText}
                    themeColor={themeColor}
                  />
                )}
              />
            </div>

            <div className="col-12">
              <label className="form-label fw-semibold">
                {isEmailChannel ? "Email preset" : "SMS preset"}{" "}
                <span className="text-danger">*</span>
              </label>
            
              <Select
                options={presetOptions}
                value={selectedPreset ?? null}
                onChange={(opt) =>
                  setForm((prev) =>
                    isEmailChannel
                      ? { ...prev, emailPreset_Guid: opt?.value ?? null }
                      : { ...prev, smsPreset_Guid: opt?.value ?? null }
                  )
                }
                styles={selectStyles}
                placeholder={
                  loadingOptions
                    ? "Loading presets..."
                    : isEmailChannel
                      ? "Search or select email template..."
                      : "Search or select SMS template..."
                }
                isDisabled={!hasChannel || loadingOptions}
                isClearable
                isSearchable
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                menuPlacement="auto"
                filterOption={(option, input) =>
                  !input?.trim() ||
                  (option.data.searchText || option.label.toLowerCase()).includes(
                    input.trim().toLowerCase()
                  )
                }
                noOptionsMessage={() =>
                  isEmailChannel ? "No email presets found" : "No SMS presets found"
                }
                formatOptionLabel={(opt, meta) => (
                  <PresetOptionLabel
                    option={opt}
                    context={meta.context}
                    mutedText={mutedText}
                    themeColor={themeColor}
                    isEmail={isEmailChannel}
                  />
                )}
              />
            </div>
          </div>
        </div>
          </>
        )}
      </div>
    </CustomModal>
  );
}

export default CampaignModel;
